import { i18next } from 'i18n/init';
import i18n from 'i18next';
import { useEffect, useState } from 'react';
import { Lang } from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import { supportedLanguages } from 'utils/languageUtils';
import * as cc from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

const CookieConsent = () => {
  const [languages, setLanguages] = useState(getLanguages());

  useEffect(() => {
    cc?.run({
      language: {
        default: getLang(),
        translations: languages,
      },
      guiOptions: { consentModal: { equalWeightButtons: false } },
      autoClearCookies: true,
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
        },
      },
    });
  }, [languages]);

  i18next.on('languageChanged', (ln) => {
    cc?.setLanguage(ln, true).catch(() => undefined);
  });

  i18next.on('loaded', () => {
    setLanguages(getLanguages());
    cc?.reset();
  });
  return null;
};

const getLanguages = () => {
  return supportedLanguages.reduce(
    (a, lng) => ({ ...a, [lng]: buildCookieConsent(lng) }),
    {}
  );
};

const buildCookieConsent = (lng: Lang): cc.Translation => {
  const t = (key: string) => i18n.t(`cookieConsent.${key}`, { lng });
  return {
    consentModal: {
      title: t('consent.title'),
      description: t('consent.description'),
      showPreferencesBtn: t('consent.letMeChoose'),
      acceptAllBtn: t('consent.primaryButtonText'),
      acceptNecessaryBtn: t('consent.secondaryButtonText'),
    } as cc.ConsentModalOptions,
    preferencesModal: {
      title: t('settings.title'),
      savePreferencesBtn: t('settings.saveButton'),
      acceptAllBtn: t('settings.acceptAllButton'),
      closeIconLabel: t('settings.closeButtonLabel'),
      sections: [
        {
          title: t('settings.blocks.header.title'),
          description: t('settings.blocks.header.description'),
        },
        {
          title: t('settings.blocks.necessary.title'),
          description: t('settings.blocks.necessary.description'),
          linkedCategory: 'necessary',
          cookieTable: {
            headers: {
              col1: t('settings.cookieTableHeaders.col1'),
              col2: t('settings.cookieTableHeaders.col2'),
              col3: t('settings.cookieTableHeaders.col3'),
              col4: t('settings.cookieTableHeaders.col4'),
            },
            body: [
              {
                col1: 'cc_cookie',
                col2: location.hostname,
                col3: t('settings.blocks.necessary.consentExpiration'),
                col4: t('settings.blocks.necessary.consentDescription'),
              },
            ],
          },
        },
        {
          title: t('settings.blocks.analytics.title'),
          description: t('settings.blocks.analytics.description'),
          linkedCategory: 'analytics',
          cookieTable: {
            headers: {
              col1: t('settings.cookieTableHeaders.col1'),
              col2: t('settings.cookieTableHeaders.col2'),
              col3: t('settings.cookieTableHeaders.col3'),
              col4: t('settings.cookieTableHeaders.col4'),
            },
            body: [
              {
                col1: '^_ga', // match all cookies starting with "_ga"
                col2: 'google.com',
                col3: t('settings.blocks.analytics.gaExpiration'),
                col4: t('settings.blocks.analytics.gaDescription'),
                is_regex: true,
              },
              {
                col1: '_gid',
                col2: 'google.com',
                col3: t('settings.blocks.analytics.gidExpiration'),
                col4: t('settings.blocks.analytics.gidDescription'),
              },
            ],
          },
        },
        {
          title: t('settings.blocks.moreInformation.title'),
          description: t('settings.blocks.moreInformation.description'),
        },
      ],
    } as cc.PreferencesModalOptions,
  };
};
export default CookieConsent;
